import { useEffect, type ChangeEvent } from 'react';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  SimpleTable,
  TableWrapperFooter,
  Pagination,
  TableFilters,
  Button,
  invoicePendingModal,
  invoiceCreateModal,
  Checkbox,
  TableHeadCell,
  TableIconsCell,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import {
  useInvoicesByClientYardsQuery,
  usePaginationState,
  useSaveFilters,
  useStatesWithFilterCounter,
} from '../../hooks';
import { CURRENCIES, TABLE } from '../../const';
import { useStore, useStoreUser } from '../../context';
import { momentTextFormat, showToRoles } from '../../utils';
import { i18n } from '../../i18n';
import { BelongsToFilterSelects } from './BelongsToFilterSelects';
import { CurrencyCode } from '../../types';
import { useNavigate, useParams } from 'react-router-dom';

interface InvoicesPageProps {
  innerScroll?: boolean;
  gridTemplateRows?: string;
}

export const InvoicesPage = styled.div<InvoicesPageProps>`
  display: grid;
  grid-template-rows: ${(props) => props.gridTemplateRows || 'max-content 1fr'};
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const InvoicesHeader = ({ totalCount, name }: { totalCount: number; name?: string }) => {
  const { clientYardId = '' } = useParams();
  const { user } = useStoreUser();

  const handleCreateInvoice = () => {
    invoiceCreateModal.names = [name || ''];
    invoiceCreateModal.clientYardIds = [clientYardId];
    invoiceCreateModal.open();
  };

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.invoices.title')}
        {name ? ` (${name})` : ''}: {totalCount}
      </Typography>
      {showToRoles('admin', user) && (
        <>
          {!name && (
            <Button
              id={'set-pending-button'}
              size={'medium'}
              onClick={() => invoicePendingModal.open()}
            >
              {i18n.t('button.setPending')}
            </Button>
          )}
          {name && (
            <Button size={'medium'} onClick={handleCreateInvoice}>
              {i18n.t('button.createInvoice')}
            </Button>
          )}
        </>
      )}
    </DashboardHeader>
  );
};

interface CheckboxActionsProps {
  data: any;
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({ data, checkIndexs }: CheckboxActionsProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const [oldFilters] = useSaveFilters('belongs-to', true);

  const handleClickAction = (val: any) => {
    const filteredData = data.filter((_: any, i: number) => checkIndexs.includes(i));

    if (val === 'create-invoice') {
      if (showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) {
        invoiceCreateModal.names = filteredData.map(
          (item: any) => `${user.yardEmployee.yard.name} <-> ${item.code}`,
        );
      } else {
        invoiceCreateModal.names = filteredData.map(
          (item: any) => `${oldFilters.yardName} <-> ${item.code}`,
        );
      }
      invoiceCreateModal.clientYardIds = filteredData.map((item: any) => item.id);
      invoiceCreateModal.open();
    }
    if (val === 'set-pending') {
      if (showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)) {
        invoicePendingModal.names = filteredData.map(
          (item: any) => `${user.yardEmployee.yard.name} <-> ${item.code}`,
        );
      } else {
        invoicePendingModal.names = filteredData.map(
          (item: any) => `${oldFilters.yardName} <-> ${item.code}`,
        );
      }
      invoicePendingModal.ids = filteredData.map((item: any) => ({
        id: item.id,
      }));
      invoicePendingModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  return (
    <>
      <Button
        size={'medium'}
        data-color={'white'}
        onClick={() => handleClickAction('create-invoice')}
      >
        {i18n.t('button.createInvoice')}
      </Button>
      <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('set-pending')}>
        {i18n.t('button.setPending')}
      </Button>
    </>
  );
};

export const InvoicesBody = ({
  setData,
  setTotalCount,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
}: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const isViewer = showToRoles(['YARD_COMPANY_VIEWER'], user);

  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState('invoices-client-yards');
  const [, setStates, filteredStates] = useStatesWithFilterCounter({
    yardId: '',
  });

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useInvoicesByClientYardsQuery({
    ...filteredStates,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    invoiceCreateModal.createMode = data.createMode;
    setTotalCount(data.totalCount || 0);
    setData(data.data ?? []);
  }, [isFetching]);

  const handleOnPageChange = (e: ChangeEvent<unknown> | null, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handlePageChange(null, 1);
    setCheckIndexs([]);
    setStates(...args);
  };

  const handleRowClick = (id: string) => {
    if (checkIndexs.length) return;
    navigate(`/dashboard/invoices/${id}`);
  };

  return (
    <SimpleTable
      data={data}
      filters={
        showToRoles(
          ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_COMPANY_VIEWER'],
          user,
        )
          ? () => (
              <TableFilters>
                <BelongsToFilterSelects
                  handleChange={(obj) => {
                    handleFilterChange('force', obj);
                  }}
                />
              </TableFilters>
            )
          : null
      }
      checkboxActions={
        isViewer
          ? undefined
          : (data) => (
              <CheckboxActions
                data={data}
                checkIndexs={checkIndexs}
                setCheckIndexs={setCheckIndexs}
                refetch={refetch}
              />
            )
      }
      tableRowProps={(cy, i) => ({
        key: cy.id || i,
        onClick: () => handleRowClick(cy.id),
      })}
      columns={[
        {
          column: 1,
          head: () => (
            <TableHeadCell align={'left'} withGap>
              {!isViewer && (
                <Checkbox
                  indeterminate={!!checkIndexs.length && checkIndexs.length !== data.data.length}
                  checked={checkIndexs.length === data.data.length && data.data.length > 0}
                  onChange={() => handleCheckboxChange()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </TableHeadCell>
          ),
          body: (_cy, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {!isViewer && (
                <Checkbox
                  checked={checkIndexs.includes(i)}
                  onChange={() => handleCheckboxChange(i)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </TableIconsCell>
          ),
          isHidden: () => showToRoles('client', user) || isViewer,
        },
        {
          headText: () =>
            showToRoles('client', user) ? i18n.t('common.yard') : i18n.t('common.clientCompany'),
          body: (cy) => (
            <TableCell component={'th'} align={'left'}>
              {cy.legalName ? cy.legalName : cy.name}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('common.client'),
          body: (cy) => (
            <TableCell component={'th'} align={'left'}>
              {cy.code}
            </TableCell>
          ),
          isHidden: () => showToRoles('client', user),
        },
        {
          headText: () => i18n.t('label.storagePaidTo'),
          body: (cy) => (
            <TableCell align={'left'}>{momentTextFormat(cy.storagePaidTo, user)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.storageAmount'),
          body: (cy) => (
            <TableCell align={'left'}>
              {cy.storageAmount}
              {CURRENCIES[cy.currencyCode as CurrencyCode]?.symbol}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.cranePaidTo'),
          body: (cy) => (
            <TableCell align={'left'}>{momentTextFormat(cy.cranePaidTo, user)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.craneAmount'),
          body: (cy) => (
            <TableCell align={'left'}>
              {cy.craneAmount}
              {CURRENCIES[cy.currencyCode as CurrencyCode]?.symbol}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.otherPaidTo'),
          body: (cy) => (
            <TableCell align={'left'}>{momentTextFormat(cy.otherPaidTo, user)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.otherAmount'),
          body: (cy) => (
            <TableCell align={'left'}>
              {cy.otherAmount}
              {CURRENCIES[cy.currencyCode as CurrencyCode]?.symbol}
            </TableCell>
          ),
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handleOnPageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
